import { Controller } from "@hotwired/stimulus"

/**
 * WordPress カテゴリ選択用 Stimulus コントローラー。
 *
 * 内部 API（`contexts` →  一覧、`roots` / `children` → 階層 select、`flat` → 単一 select、
 * `search` → 名前検索、`selection` → 既存 category_id から UI 復元）と連携する。
 *
 * ユーザーの操作フローは「投稿タイプ → タクソノミー（taxonomy + rest_base）→ 用語の選択」。
 * タクソノミーが階層的な場合は `cascadeContainer` に段階的な select を積み、
 * 非階層の場合は `flatSelect` に一覧を載せる。
 *
 * - **hiddenId**: フォーム送信に使う `WordPressCategory` のレコード id（アプリ側の primary key）
 * - **currentMeta**: 各 fetch のクエリ用（post_type, taxonomy, rest_base, hierarchical）
 * - **interactionFieldset**: ターム（flat / cascade）と名前検索を内包。投稿タイプ＋タクソノミーが揃うまで `disabled`
 * - タクソノミー `<option>` の value は `taxonomy|||rest_base|||0|1` 形式で階層フラグを保持する
 */
export default class extends Controller {
  static values = {
    contextsUrl: String,
    rootsUrl: String,
    childrenUrl: String,
    flatUrl: String,
    searchUrl: String,
    selectionUrl: String,
    /** error_class(post_setting, :word_press_category_id) 相当（例: is-invalid）。動的に生成する階層 select に付与する */
    selectInvalidClass: { type: String, default: "" },
  }

  static targets = [
    "hiddenId",
    "postType",
    "taxonomy",
    "interactionFieldset",
    "termPlaceholder",
    "cascadeContainer",
    "flatSelect",
    "flatWrapper",
    "flatTruncatedHint",
    "searchInput",
    "searchResults",
  ]

  /** 接続時: 状態を初期化し、文脈取得とブートストラップを開始する。 */
  connect() {
    this.contextsPayload = null
    this.currentMeta = null
    this.searchTimer = null
    this.bootstrap()
  }

  /** 切断時: 検索デバウンス用タイマーを解除する。 */
  disconnect() {
    if (this.searchTimer) clearTimeout(this.searchTimer)
  }

  /**
   * 初回処理: contexts API で一覧を取得する。
   * hidden に既存の category id があれば `applySelection` で全体を復元、なければ投稿タイプだけ埋める。
   */
  async bootstrap() {
    try {
      await this.loadContexts()
      const initialId = this.hiddenIdTarget.value?.trim()
      if (initialId) {
        await this.applySelection(initialId)
      } else {
        this.populatePostTypes()
        this.resetTaxonomyPlaceholder()
        this.syncInteractionEnabled()
      }
    } catch (e) {
      console.error("wp-category-cascade bootstrap failed", e)
    }
  }

  /** GET contexts: `post_types` / `contexts` を取得して `contextsPayload` に保持する。 */
  async loadContexts() {
    const res = await fetch(this.contextsUrlValue, {
      headers: { Accept: "application/json" },
    })
    if (!res.ok) throw new Error("contexts failed")
    this.contextsPayload = await res.json()
  }

  /** 投稿タイプの select を API の `post_types` から再構築する（先頭に「設定しない」）。可能なら直前の選択を維持。 */
  populatePostTypes() {
    const select = this.postTypeTarget
    const current = select.value
    select.innerHTML = ""
    const none = document.createElement("option")
    none.value = ""
    none.textContent = "設定しない"
    select.appendChild(none)
    for (const pt of this.contextsPayload.post_types || []) {
      const opt = document.createElement("option")
      opt.value = pt
      opt.textContent = pt
      select.appendChild(opt)
    }
    if (current && [...select.options].some((o) => o.value === current)) {
      select.value = current
    }
  }

  /**
   * 投稿タイプとタクソノミーの両方が選ばれているときだけ、ターム・検索の fieldset を有効にする。
   */
  syncInteractionEnabled() {
    if (!this.hasInteractionFieldsetTarget) return
    const enabled =
      Boolean(this.postTypeTarget.value?.trim()) && Boolean(this.taxonomyTarget.value?.trim())
    this.interactionFieldsetTarget.disabled = !enabled
  }

  /** タクソノミー select を無効化し、プレースホルダのみに戻す。 */
  resetTaxonomyPlaceholder() {
    this.taxonomyTarget.innerHTML = ""
    this.taxonomyTarget.disabled = true
    const ph = document.createElement("option")
    ph.value = ""
    ph.textContent = "先に投稿タイプを選択"
    this.taxonomyTarget.appendChild(ph)
  }

  /** 投稿タイプ変更時: 検索 UI を消し、未選択ならタクソノミー・用語をリセット。選択時は該当タクソノミー一覧を出す。 */
  postTypeChanged() {
    const pt = this.postTypeTarget.value
    this.clearSearchUi()
    if (!pt) {
      this.resetTaxonomyPlaceholder()
      this.hiddenIdTarget.value = ""
      this.clearTermUi()
      this.syncInteractionEnabled()
      return
    }
    this.taxonomyTarget.disabled = false
    this.fillTaxonomyOptions(pt)
    this.hiddenIdTarget.value = ""
    this.clearTermUi()
    this.syncInteractionEnabled()
  }

  /** 指定投稿タイプに紐づく `contexts` だけでタクソノミー option を組み立てる（value に階層フラグを含む）。 */
  fillTaxonomyOptions(postType) {
    const select = this.taxonomyTarget
    select.innerHTML = ""
    const ph = document.createElement("option")
    ph.value = ""
    ph.textContent = "タクソノミーを選択"
    select.appendChild(ph)
    const list = (this.contextsPayload.contexts || []).filter((c) => c.post_type === postType)
    for (const ctx of list) {
      const opt = document.createElement("option")
      opt.value = `${ctx.taxonomy}|||${ctx.rest_base}|||${ctx.hierarchical ? "1" : "0"}`
      opt.textContent = `${ctx.taxonomy} (${ctx.rest_base})`
      select.appendChild(opt)
    }
  }

  /**
   * タクソノミー変更時: `currentMeta` を更新し、階層ならカスケード＋ルート取得、
   * 非階層ならフラット select の表示と一覧取得。
   */
  taxonomyChanged() {
    const raw = this.taxonomyTarget.value
    this.clearSearchUi()
    this.hiddenIdTarget.value = ""
    this.clearTermUi()
    if (!raw) {
      this.syncInteractionEnabled()
      return
    }

    const [taxonomy, restBase, h] = raw.split("|||")
    const hierarchical = h === "1"
    this.currentMeta = {
      post_type: this.postTypeTarget.value,
      taxonomy,
      rest_base: restBase,
      hierarchical,
    }
    if (hierarchical) {
      this.showCascade()
      this.loadRootsAndBuildFirstLevel()
    } else {
      this.showFlat()
      this.loadFlatOptions()
    }
    this.syncInteractionEnabled()
  }

  hideTermPlaceholder() {
    if (this.hasTermPlaceholderTarget) {
      this.termPlaceholderTarget.classList.add("d-none")
    }
  }

  showTermPlaceholder() {
    if (this.hasTermPlaceholderTarget) {
      this.termPlaceholderTarget.classList.remove("d-none")
    }
  }

  /** 階層 UI を表示し、フラット用ラッパーを隠す。 */
  showCascade() {
    this.hideTermPlaceholder()
    this.flatWrapperTarget.classList.add("d-none")
    this.cascadeContainerTarget.classList.remove("d-none")
  }

  /** カスケード領域を空にして隠し、フラット select 用ラッパーを表示する。 */
  showFlat() {
    this.hideTermPlaceholder()
    this.cascadeContainerTarget.innerHTML = ""
    this.cascadeContainerTarget.classList.add("d-none")
    this.flatWrapperTarget.classList.remove("d-none")
  }

  /** 用語選択 UI をすべて消す（カスケード・フラット・currentMeta・切り詰めヒント）。プレースホルダのターム欄を再表示する。 */
  clearTermUi() {
    this.cascadeContainerTarget.innerHTML = ""
    this.cascadeContainerTarget.classList.add("d-none")
    this.flatWrapperTarget.classList.add("d-none")
    this.flatSelectTarget.innerHTML = ""
    this.currentMeta = null
    if (this.hasFlatTruncatedHintTarget) {
      this.flatTruncatedHintTarget.classList.add("d-none")
    }
    this.showTermPlaceholder()
  }

  /** 現在の文脈を API クエリ用の URLSearchParams にする。未確定時は null。 */
  contextQueryParams() {
    if (!this.currentMeta) return null
    const p = new URLSearchParams()
    p.set("post_type", this.currentMeta.post_type)
    p.set("taxonomy", this.currentMeta.taxonomy)
    p.set("rest_base", this.currentMeta.rest_base)
    return p
  }

  /** GET roots: ルートカテゴリで第1段の select を生成し、hidden を連鎖ルールに合わせて更新する。 */
  async loadRootsAndBuildFirstLevel() {
    const p = this.contextQueryParams()
    if (!p) return
    const url = `${this.rootsUrlValue}?${p.toString()}`
    const res = await fetch(url, { headers: { Accept: "application/json" } })
    if (!res.ok) return
    const data = await res.json()
    this.cascadeContainerTarget.innerHTML = ""
    this.appendLevelSelect(data.categories || [], null, 0)
    this.updateHiddenFromCascade()
  }

  /** GET flat: 非階層用の option 一覧を組み立て、`truncated` に応じてヒントを表示する。 */
  async loadFlatOptions() {
    const p = this.contextQueryParams()
    if (!p) return
    const url = `${this.flatUrlValue}?${p.toString()}`
    const res = await fetch(url, { headers: { Accept: "application/json" } })
    if (!res.ok) return
    const data = await res.json()
    const sel = this.flatSelectTarget
    sel.innerHTML = ""
    const ph = document.createElement("option")
    ph.value = ""
    ph.textContent = "選択してください"
    sel.appendChild(ph)
    for (const row of data.categories || []) {
      const opt = document.createElement("option")
      opt.value = String(row.id)
      opt.textContent = row.name
      sel.appendChild(opt)
    }
    if (this.hasFlatTruncatedHintTarget) {
      if (data.truncated) {
        this.flatTruncatedHintTarget.classList.remove("d-none")
      } else {
        this.flatTruncatedHintTarget.classList.add("d-none")
      }
    }
  }

  /** フラット select の値を hidden に反映する（data-action から呼ばれる想定）。 */
  flatSelectChanged() {
    this.hiddenIdTarget.value = this.flatSelectTarget.value
  }

  /**
   * カスケードの1段分: `categories` から select を生成し `cascadeContainer` に追加する。
   * `selectedId` があればその option を選択状態にする。子取得用に wp_category_id / has_children を data 属性に載せる。
   */
  appendLevelSelect(categories, selectedId, depth) {
    const wrap = document.createElement("div")
    wrap.className = "mb-2"
    const sel = document.createElement("select")
    sel.className = this.termSelectClassName()
    sel.dataset.level = String(depth)
    sel.dataset.action = "change->wp-category-cascade#cascadeLevelChanged"

    const ph = document.createElement("option")
    ph.value = ""
    ph.textContent = depth === 0 ? "階層を選択" : "子カテゴリを選択"
    sel.appendChild(ph)

    for (const c of categories) {
      const opt = document.createElement("option")
      opt.value = String(c.id)
      opt.textContent = c.name
      opt.dataset.wpCategoryId = String(c.wp_category_id)
      if (c.has_children) opt.dataset.hasChildren = "1"
      sel.appendChild(opt)
    }

    if (selectedId) sel.value = String(selectedId)

    wrap.appendChild(sel)
    this.cascadeContainerTarget.appendChild(wrap)
  }

  /** ターム用 select の class（form-control 系と error_class をフォーム側と揃える） */
  termSelectClassName() {
    const extra = this.selectInvalidClassValue?.trim()
    return extra ? `form-select ${extra}` : "form-select"
  }

  /**
   * 階層 select 変更時: 変更段より下の段を削除し、子がある選択なら GET children で次の段を追加。
   * hidden は「先頭から連続して値が入っている最後の select」の id とする。
   */
  async cascadeLevelChanged(event) {
    const changed = event.target
    const depth = parseInt(changed.dataset.level, 10)
    this.removeLevelsAfter(depth)

    const opt = changed.selectedOptions[0]
    if (!changed.value) {
      this.updateHiddenFromCascade()
      return
    }

    if (opt?.dataset?.hasChildren === "1" && opt?.dataset?.wpCategoryId) {
      const p = this.contextQueryParams()
      if (!p) return
      p.set("parent_wp_id", opt.dataset.wpCategoryId)
      const url = `${this.childrenUrlValue}?${p.toString()}`
      const res = await fetch(url, { headers: { Accept: "application/json" } })
      if (!res.ok) {
        this.updateHiddenFromCascade()
        return
      }
      const data = await res.json()
      if ((data.categories || []).length > 0) {
        this.appendLevelSelect(data.categories, null, depth + 1)
      }
    }

    this.updateHiddenFromCascade()
  }

  /** 指定 depth より深い段の select ラッパーを DOM から取り除く。 */
  removeLevelsAfter(depth) {
    const wraps = this.cascadeContainerTarget.querySelectorAll("div")
    wraps.forEach((w) => {
      const s = w.querySelector("select")
      if (s && parseInt(s.dataset.level, 10) > depth) w.remove()
    })
  }

  /** カスケード各段の値から、連続して選ばれている最後のカテゴリ id を hidden に書き込む。 */
  updateHiddenFromCascade() {
    const selects = [...this.cascadeContainerTarget.querySelectorAll("select[data-level]")]
    let last = ""
    for (const s of selects) {
      if (s.value) last = s.value
      else break
    }
    this.hiddenIdTarget.value = last
  }

  /** 検索入力: 2文字未満は結果クリア、それ以上は 300ms デバウンス後に `runSearch`。 */
  searchInput() {
    if (!this.currentMeta) return
    const q = this.searchInputTarget.value.trim()
    if (this.searchTimer) clearTimeout(this.searchTimer)
    if (q.length < 2) {
      this.searchResultsTarget.innerHTML = ""
      return
    }
    this.searchTimer = setTimeout(() => this.runSearch(q), 300)
  }

  /** GET search: クエリ `q` で候補を取り、リスト描画に渡す。 */
  async runSearch(q) {
    const p = this.contextQueryParams()
    if (!p) return
    p.set("q", q)
    const url = `${this.searchUrlValue}?${p.toString()}`
    const res = await fetch(url, { headers: { Accept: "application/json" } })
    if (!res.ok) return
    const data = await res.json()
    this.renderSearchResults(data.categories || [])
  }

  /** 検索結果をボタン一覧で描画。クリックで `pickSearchResult` に繋ぐ。 */
  renderSearchResults(items) {
    const ul = this.searchResultsTarget
    ul.innerHTML = ""
    ul.className = "list-group mt-1"
    for (const item of items) {
      const li = document.createElement("button")
      li.type = "button"
      li.className = "list-group-item list-group-item-action text-start py-2"
      li.textContent = item.full_path ? `${item.full_path}` : item.name
      li.addEventListener("click", () => this.pickSearchResult(item.id))
      ul.appendChild(li)
    }
  }

  /** 検索で1件選んだとき: hidden をセットし入力を消してから、カスケード／フラットを `applySelection` で揃える。 */
  pickSearchResult(id) {
    this.hiddenIdTarget.value = String(id)
    this.searchResultsTarget.innerHTML = ""
    this.searchInputTarget.value = ""
    this.applySelection(String(id))
  }

  /** 投稿タイプ／タクソノミー変更時など、検索欄と結果リストだけを空にする。 */
  clearSearchUi() {
    if (this.hasSearchInputTarget) this.searchInputTarget.value = ""
    if (this.hasSearchResultsTarget) this.searchResultsTarget.innerHTML = ""
  }

  /**
   * GET selection: サーバーが返す post_type / taxonomy / 各段の options で UI を再構築する。
   * 階層は `levels` をそのまま段階 select にし、非階層は flat を読み直して `category_id` を選択する。
   */
  async applySelection(categoryId) {
    const url = `${this.selectionUrlValue}?category_id=${encodeURIComponent(categoryId)}`
    const res = await fetch(url, { headers: { Accept: "application/json" } })
    if (!res.ok) {
      this.populatePostTypes()
      this.resetTaxonomyPlaceholder()
      this.clearTermUi()
      this.syncInteractionEnabled()
      return
    }
    const data = await res.json()

    this.populatePostTypes()
    this.postTypeTarget.value = data.post_type
    this.taxonomyTarget.disabled = false
    this.fillTaxonomyOptions(data.post_type)
    const taxVal = `${data.taxonomy}|||${data.rest_base}|||${data.hierarchical ? "1" : "0"}`
    this.taxonomyTarget.value = taxVal
    this.currentMeta = {
      post_type: data.post_type,
      taxonomy: data.taxonomy,
      rest_base: data.rest_base,
      hierarchical: data.hierarchical,
    }

    if (data.hierarchical) {
      this.showCascade()
      this.cascadeContainerTarget.innerHTML = ""
      const levels = data.levels || []
      levels.forEach((level, idx) => {
        const opts = level.options || []
        this.appendLevelSelect(opts, level.selected_id, idx)
      })
      this.updateHiddenFromCascade()
    } else {
      this.showFlat()
      await this.loadFlatOptions()
      this.flatSelectTarget.value = String(data.category_id)
      this.hiddenIdTarget.value = String(data.category_id)
    }
    this.syncInteractionEnabled()
  }
}
